import React, { useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const isValidUrl = (value) => {
  if (!value || !value.trim()) {
    return true;
  }

  return /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/.test(
    value,
  );
};

const ServiceEditDialog = ({ open, onOpenChange, service, changeReload }) => {
  const { t } = useTranslation();

  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [workplaces, setWorkplaces] = useState([]);
  const [updatingWorkplaceIds, setUpdatingWorkplaceIds] = useState([]);

  const { post: postLink, response: postLinkResponse } = useFetch(
    '/ServicesDescription/UrlUpdate',
  );

  const { post: updateWorkplaceShowInQueue } = useFetch(
    '/ServicesDescription/UpdateShowInQueue',
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setLink(service?.descriptionUrl || '');
    setLinkError('');
    setErrorMessage('');
    setWorkplaces(service?.workplaces ?? []);
    setUpdatingWorkplaceIds([]);
  }, [open, service]);

  const title = useMemo(
    () => service?.title?.toString().toUpperCase() || '',
    [service],
  );

  if (!service) {
    return null;
  }

  const updateLocalWorkplace = (workplaceId, showInQueue) => {
    setWorkplaces((prev) =>
      prev.map((item) =>
        item.workplaceId === workplaceId ? { ...item, showInQueue } : item,
      ),
    );
  };

  const handleSaveLink = async () => {
    setErrorMessage('');
    setLinkError('');

    if (!isValidUrl(link)) {
      setLinkError('Įveskite teisingą url');
      return;
    }

    setSavingLink(true);

    try {
      await postLink('', {
        link: link?.trim() || '',
        serviceExternalId: service.serviceExternalId,
      });

      if (postLinkResponse.status >= 400) {
        setErrorMessage('Nepavyko išsaugoti nuorodos');
        return;
      }

      changeReload();
      onOpenChange(false);
    } catch (error) {
      setErrorMessage('Nepavyko išsaugoti nuorodos');
    } finally {
      setSavingLink(false);
    }
  };

  const handleWorkplaceQueueChange = async (workplace, checked) => {
    const workplaceId = workplace.workplaceId;
    const previousValue = workplace.showInQueue;

    setUpdatingWorkplaceIds((prev) =>
      prev.includes(workplaceId) ? prev : [...prev, workplaceId],
    );
    updateLocalWorkplace(workplaceId, checked);
    setErrorMessage('');

    try {
      await updateWorkplaceShowInQueue(
        `?serviceId=${service.serviceExternalId}&workplaceId=${workplaceId}&flag=${checked}`,
      );
    } catch (error) {
      updateLocalWorkplace(workplaceId, previousValue);
      setErrorMessage('Nepavyko atnaujinti eilės rodymo reikšmės');
    } finally {
      setUpdatingWorkplaceIds((prev) =>
        prev.filter((id) => id !== workplaceId),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-[760px] p-0 overflow-hidden sm:w-full max-h-[90dvh] flex flex-col gap-0">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6 sm:pt-6 sm:pb-4">
          <DialogTitle className="pr-8 break-words text-lg sm:text-xl font-bold">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Paslaugos nuorodos ir darbo vietų nustatymų redagavimas
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="service-description-link">
              {t('userAccount.serviceInfo.newLink')}
            </Label>

            <Input
              id="service-description-link"
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />

            {linkError ? (
              <p className="text-sm text-destructive">{linkError}</p>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="text-base font-semibold">Darbo vietos</div>

            {workplaces.length > 0 ? (
              <div className="space-y-3">
                {workplaces.map((wp) => {
                  const isUpdating = updatingWorkplaceIds.includes(
                    wp.workplaceId,
                  );

                  return (
                    <div
                      key={wp.workplaceId}
                      className={`rounded-xl border p-4 transition-opacity ${
                        isUpdating ? 'opacity-60' : 'opacity-100'
                      }`}
                    >
                      <div className="mb-3 flex flex-col gap-1">
                        <div className="font-semibold">
                          {wp.department || '—'}
                        </div>

                        {wp.specialistName ? (
                          <div className="text-sm text-muted-foreground break-words">
                            {wp.specialistName}
                          </div>
                        ) : null}
                      </div>

                      <label
                        htmlFor={`workplace-show-in-queue-${wp.workplaceId}`}
                        className={`inline-flex items-center gap-2 ${
                          isUpdating ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          id={`workplace-show-in-queue-${wp.workplaceId}`}
                          type="checkbox"
                          checked={!!wp.showInQueue}
                          disabled={isUpdating}
                          onChange={(e) =>
                            handleWorkplaceQueueChange(wp, e.target.checked)
                          }
                          className="h-4 w-4 shrink-0"
                        />
                        <span className="text-sm font-medium">
                          Rodyti eilėje
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Nėra darbo vietų
              </div>
            )}
          </div>

          {errorMessage ? (
            <p className="text-sm text-destructive">{errorMessage}</p>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveLink}
            disabled={savingLink}
            className="w-full sm:w-auto min-w-[120px]"
          >
            {savingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Išsaugoti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceEditDialog;
